import { useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Sparkles, Check, Lock, Zap, Star, Shield, Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const FREE_FEATURES = [
  'One sport (Football, Soccer, or Basketball)',
  'Beginner & intermediate lessons',
  'Daily challenges',
  'Game IQ tracking',
  'Community leaderboards',
];

const PRO_FEATURES = [
  'All sports unlocked — switch anytime',
  'Advanced & expert-level lessons',
  'Multi-sport progress dashboard',
  'Downloadable playbooks & cheat sheets',
  'Certificates of completion',
  'Priority access to new sports',
  'Formation Lab (coming soon)',
];

const FOUNDING_PERKS = [
  'Lock in $3.99/month forever — never goes up',
  'Everything in Pro, from day one',
  '"Founding Member" badge on your profile',
  'Early access to every new feature',
  'Direct input on what we build next',
];

export function PricingPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleComingSoon = () => {
    toast.info("Subscriptions launching soon! We'll notify you when it's live. 🚀");
  };

  const handleWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    const { error } = await (supabase as any).from('waitlist').insert({ email: trimmed });
    setSubmitting(false);
    if (error) {
      if (error.code === '23505') {
        toast.info("You're already on the waitlist! We'll be in touch. 🎉");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
      return;
    }
    setEmail('');
    toast.success("You're on the list! We'll notify you at launch. 🚀");
  };

  return (
    <div className="space-y-8 pb-8">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-3"
      >
        <h1 className="font-display font-bold text-2xl">Simple Pricing</h1>
        <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
          Start free. Upgrade when you're ready to unlock every sport and go deeper.
        </p>
      </motion.div>

      {/* Founding Member Banner */}
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-accent/15 via-accent/8 to-transparent border-2 border-accent/30 p-5"
      >
        <div className="absolute top-0 right-0 bg-accent text-accent-foreground px-3 py-1 rounded-bl-xl">
          <span className="font-display font-bold text-[10px] uppercase tracking-wider">Limited</span>
        </div>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h2 className="font-display font-bold text-lg">Become a Founding Member</h2>
            <p className="font-body text-xs text-muted-foreground mt-0.5">
              Help us build the future of sports education
            </p>
          </div>
        </div>

        <p className="font-body text-sm text-foreground/80 leading-relaxed mb-4">
          We're looking for our first <span className="font-bold text-accent">200 members</span> to join us on this journey. 
          As a Founding Member, you'll lock in Pro at our lowest price — <span className="font-bold">forever</span> — and 
          help shape what Sportara becomes.
        </p>

        <div className="space-y-2 mb-5">
          {FOUNDING_PERKS.map((perk, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + i * 0.04 }}
              className="flex items-start gap-2"
            >
              <Star className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" />
              <span className="font-body text-sm text-foreground/80">{perk}</span>
            </motion.div>
          ))}
        </div>

        <div className="flex items-baseline gap-1.5 mb-3">
          <span className="font-display font-bold text-3xl text-accent">$3.99</span>
          <span className="font-body text-sm text-muted-foreground">/month forever</span>
        </div>

        <form onSubmit={handleWaitlist} className="flex gap-2">
          <Input
            type="email"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 h-12 rounded-xl bg-background/60 border-accent/30 font-body placeholder:text-muted-foreground/60"
            disabled={submitting}
          />
          <Button
            type="submit"
            disabled={submitting}
            className="h-12 px-5 font-display font-bold bg-accent hover:bg-accent/90 text-accent-foreground rounded-xl shrink-0"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="mr-1.5 w-4 h-4" />}
            {submitting ? '' : 'Join'}
          </Button>
        </form>
        <p className="text-center text-[10px] text-muted-foreground mt-2 font-body">
          We'll email you the moment subscriptions go live
        </p>
      </motion.div>

      {/* Plans Grid */}
      <div className="space-y-4">
        {/* Free Plan */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl border border-border bg-card p-5"
        >
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-muted-foreground" />
            <h3 className="font-display font-bold text-base">Free</h3>
          </div>
          <p className="font-body text-xs text-muted-foreground mb-3">Perfect for getting started</p>

          <div className="flex items-baseline gap-1 mb-4">
            <span className="font-display font-bold text-2xl">$0</span>
            <span className="font-body text-sm text-muted-foreground">/forever</span>
          </div>

          <div className="space-y-2 mb-4">
            {FREE_FEATURES.map((feature, i) => (
              <div key={i} className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                <span className="font-body text-sm text-foreground/80">{feature}</span>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            className="w-full h-10 font-display font-bold rounded-xl border-2"
            disabled
          >
            Current Plan
          </Button>
        </motion.div>

        {/* Pro Plan */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-2xl border-2 border-primary/30 bg-card p-5 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-3 py-1 rounded-bl-xl">
            <span className="font-display font-bold text-[10px] uppercase tracking-wider">Popular</span>
          </div>

          <div className="flex items-center gap-2 mb-1">
            <Crown className="w-4 h-4 text-primary" />
            <h3 className="font-display font-bold text-base">Pro</h3>
          </div>
          <p className="font-body text-xs text-muted-foreground mb-3">For serious fans & students of the game</p>

          <div className="flex items-baseline gap-1 mb-1">
            <span className="font-display font-bold text-2xl">$6.99</span>
            <span className="font-body text-sm text-muted-foreground">/month</span>
          </div>
          <p className="font-body text-xs text-muted-foreground mb-4">
            or $49.99/year <span className="text-primary font-semibold">(save 40%)</span>
          </p>

          <div className="space-y-2 mb-5">
            {PRO_FEATURES.map((feature, i) => (
              <div key={i} className="flex items-start gap-2">
                <Zap className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                <span className="font-body text-sm text-foreground/80">{feature}</span>
              </div>
            ))}
          </div>

          <Button
            onClick={handleComingSoon}
            className="w-full h-12 text-base font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
          >
            <Lock className="mr-2 w-4 h-4" />
            Coming Soon
          </Button>
        </motion.div>
      </div>

      {/* Bottom note */}
      <p className="text-center font-body text-xs text-muted-foreground leading-relaxed px-4">
        Sportara is in beta. All plans will be available once we launch. 
        Founding Member spots are limited to 200 — first come, first served.
      </p>
    </div>
  );
}
